import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { t, getLocale, setLocale, LANGUAGES, LOCALE_STORAGE_KEY } from '../../i18n';
import { icon } from '../ui/icons';
import { showToast } from '../ui/toast';

/**
 * <cadence-lang-menu> — header control next to the theme toggle that switches the
 * UI language. Changing language saves the preference (PATCH /auth/me) and reloads
 * so the whole app re-renders in the new locale. Shows a globe + the current code.
 */
@customElement('cadence-lang-menu')
export class CadenceLangMenu extends LitElement {
  /** Render the trigger for a dark/colored background (e.g. the login brand band). */
  @property({ type: Boolean }) dark = false;
  @state() private open = false;

  static styles = css`
    :host { position: relative; display: inline-block; }
    .trigger {
      display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
      background: none; border: 1px solid var(--color-border); color: var(--color-text-secondary);
      border-radius: var(--radius-md); height: 38px; min-width: 44px; padding: 0 10px; font: inherit;
      transition: border-color 0.15s, color 0.15s;
    }
    .trigger:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .trigger.dark { border-color: rgba(255, 255, 255, 0.5); color: #fff; }
    .trigger.dark:hover { border-color: #fff; color: #fff; background: rgba(255, 255, 255, 0.12); }
    .code { font-size: 0.82rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }

    .backdrop { position: fixed; inset: 0; z-index: 40; }
    .menu {
      position: absolute; top: calc(100% + 8px); right: 0; z-index: 50; min-width: 160px;
      background: var(--color-bg-primary); border: 1px solid var(--color-border);
      border-radius: var(--radius-md); box-shadow: var(--shadow-lg); overflow: hidden; padding: 6px;
    }
    .item {
      display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
      background: none; border: none; cursor: pointer; font: inherit; color: var(--color-text-primary);
      padding: 9px 10px; border-radius: var(--radius-sm);
    }
    .item:hover { background: var(--color-bg-secondary); }
    .item .tick { margin-left: auto; color: var(--color-primary); display: inline-flex; }
    .item .tick svg { width: 16px; height: 16px; }
  `;

  private async changeLanguage(code: string) {
    this.open = false;
    if (code === getLocale()) return;
    // Remember the choice locally so it survives the reload and the login boundary
    // (boot reads it pre-login). When signed in, also persist it to the account.
    try { localStorage.setItem(LOCALE_STORAGE_KEY, code); } catch { /* storage unavailable */ }
    try {
      if (store.get().user) await api.patch('/auth/me', { language_preference: code });
      setLocale(code);
      location.reload();
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  }

  render() {
    const cur = getLocale();
    return html`
      <button class="trigger ${this.dark ? 'dark' : ''}" aria-haspopup="menu" aria-expanded=${this.open ? 'true' : 'false'}
        aria-label=${t('common.header.language')} title=${t('common.header.language')}
        @click=${() => (this.open = !this.open)}>
        <span class="code">${cur}</span>
      </button>
      ${this.open ? html`
        <div class="backdrop" @click=${() => (this.open = false)}></div>
        <div class="menu" role="menu">
          ${LANGUAGES.map((l) => html`
            <button class="item" role="menuitemradio" aria-checked=${cur === l.code ? 'true' : 'false'} @click=${() => this.changeLanguage(l.code)}>
              ${l.label}
              ${cur === l.code ? html`<span class="tick">${icon('check', 16)}</span>` : ''}
            </button>`)}
        </div>` : ''}
    `;
  }
}
